import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api, jsonBody } from "../../api/client";
import { BrandLockup } from "../../brand/BrandMark";
import { ErrorPanel, Field, FormActions, Notice } from "../../ui/components";
import { BoundarySkeleton } from "../../ui/skeletons";

interface InvitationPreview {
  tenantSlug: string;
  customerName: string;
  requiresPassword: boolean;
}
interface Acceptance {
  tenantSlug: string;
  customerName: string;
  requiresLogin: boolean;
}

export function PortalInvitationPage() {
  const { tenantSlug = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.hash.slice(1)).get("token") ?? "";
  const preview = useQuery({
    queryKey: ["portal", "invitation", token],
    queryFn: () =>
      api<InvitationPreview>("/api/v1/portal/invitations/preview", {
        method: "POST",
        body: jsonBody({ token }),
      }),
    enabled: token.length >= 32,
    retry: false,
  });
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const [accepted, setAccepted] = useState<Acceptance | null>(null);

  async function accept(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setWorking(true);
    setError(undefined);
    try {
      setAccepted(
        await api<Acceptance>("/api/v1/portal/invitations/accept", {
          method: "POST",
          body: jsonBody({ token, password: String(form.get("password")) }),
        }),
      );
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (!token)
    return (
      <main className="auth-panel">
        <div className="auth-card">
          <Notice tone="danger">This portal invitation link is incomplete.</Notice>
        </div>
      </main>
    );
  if (preview.isLoading) return <BoundarySkeleton />;
  if (preview.error)
    return (
      <main className="auth-panel">
        <div className="auth-card">
          <ErrorPanel error={preview.error} />
        </div>
      </main>
    );
  return (
    <main className="auth-layout">
      <section className="auth-brand">
        <BrandLockup />
        <div>
          <h1>One secure place for your venue bookings.</h1>
          <p>Review dates, invoices, documents, choices, and messages shared by the venue team.</p>
        </div>
        <footer>The invitation token is single-use and expires automatically.</footer>
      </section>
      <section className="auth-panel">
        <div className="auth-card">
          <h1>Accept portal access</h1>
          <p>
            {preview.data?.customerName} · {preview.data?.tenantSlug || tenantSlug}
          </p>
          {error ? <ErrorPanel error={error} /> : null}
          {accepted ? (
            <div className="stack">
              <Notice tone="success">Portal access accepted for {accepted.customerName}.</Notice>
              <button
                className="button primary"
                type="button"
                onClick={() =>
                  navigate("/login", {
                    replace: true,
                    state: { from: `/portal/${accepted.tenantSlug}` },
                  })
                }
              >
                {accepted.requiresLogin ? "Sign in to portal" : "Open portal"}
              </button>
            </div>
          ) : (
            <form className="form-stack" onSubmit={accept}>
              {preview.data?.requiresPassword ? (
                <Field
                  label="Create password"
                  hint="Use at least 12 characters and a password manager."
                >
                  <input
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    minLength={12}
                    required
                  />
                </Field>
              ) : null}
              <FormActions submitting={working} submitLabel="Accept invitation" />
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
